import { useEffect, useCallback, useMemo } from 'react';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller/src/index.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Item } from '@atlassian/timeline-table/common/types/item';
import { useHierarchyStore } from '../hierarchy/index.tsx';
import { useTimelineViewIssues } from '../timeline-view-issues/index.tsx';

const issueHasChildren = (issue: Item) => issue.childItems && issue.childItems.length > 0;

export const ExpandCollapseAllController = () => {
	const [{ childrenQueryData, expandedItems }, { setIssueExpanded, clearExpandedItems }] =
		useHierarchyStore();

	const handleCollapseAllItems = useCallback(() => {
		clearExpandedItems();
	}, [clearExpandedItems]);

	const [
		{ shouldExpandAllItems },
		{ setHasExpandedAllItems, setHasCollapsedAllItems, disableExpandCollapseAllItems },
	] = useExpandCollapseAllItems({
		view: 'timeline',
		onCollapse: handleCollapseAllItems,
	});

	const { flattenedItems } = useTimelineViewIssues({ childrenQueryData, expandedItems });

	useEffect(() => {
		disableExpandCollapseAllItems();
	}, [disableExpandCollapseAllItems]);

	const handleExpandAllItems = useCallback(() => {
		if (shouldExpandAllItems) {
			flattenedItems.forEach((item) => {
				const isIssueExpanded = expandedItems[toIssueId(item.id)] === true;
				const hasChildren = issueHasChildren(item);
				if (hasChildren && !isIssueExpanded) {
					setIssueExpanded(toIssueId(item.id), true);
				}
			});
		}
	}, [expandedItems, flattenedItems, setIssueExpanded, shouldExpandAllItems]);

	const hasNestedItems = useMemo(
		() => flattenedItems.some((item) => issueHasChildren(item)),
		[flattenedItems],
	);

	const hasExpandedAllItems = useMemo(() => {
		const checkItemsExpanded: (item: Item) => boolean = (item) => {
			if (!issueHasChildren(item)) {
				return true;
			}
			const isExpanded = expandedItems[toIssueId(item.id)] === true;

			if (isExpanded) {
				return !!item.childItems?.every((child) => checkItemsExpanded(child));
			}
			return shouldExpandAllItems;
		};
		return hasNestedItems && flattenedItems.every((item) => checkItemsExpanded(item));
	}, [expandedItems, flattenedItems, hasNestedItems, shouldExpandAllItems]);

	const hasCollapsedAllItems = useMemo(
		() =>
			hasNestedItems && flattenedItems.every((item) => expandedItems[toIssueId(item.id)] !== true),
		[expandedItems, flattenedItems, hasNestedItems],
	);

	useEffect(() => {
		setHasExpandedAllItems(hasExpandedAllItems);
	}, [hasExpandedAllItems, setHasExpandedAllItems]);

	useEffect(() => {
		setHasCollapsedAllItems(hasCollapsedAllItems);
	}, [hasCollapsedAllItems, setHasCollapsedAllItems]);

	useEffect(() => {
		handleExpandAllItems();
	}, [handleExpandAllItems]);

	return null;
};
